// Local JWT decoder — never verifies the signature (that's the API's job);
// only reads claims so the client can read role, tenant, expiry.

export interface JwtClaims {
  sub?: string;
  exp?: number;
  iat?: number;
  iss?: string;
  type?: string;
  tenant_id?: number;
  tenant_slug?: string;
  role?: string;
  roles?: string[];
  email?: string;
  // Present only on operator-impersonation tokens minted by auth-service:
  // impersonated_by = the operator's user id, impersonation_mode = view scope.
  impersonated_by?: number;
  impersonation_mode?: "read_only" | "act_as";
}

function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  const decoded = atob(padded + pad);
  // Handle UTF-8.
  try {
    return decodeURIComponent(
      Array.from(decoded)
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
  } catch {
    return decoded;
  }
}

export function decodeJwt(token: string): JwtClaims | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  const payloadSegment = parts[1];
  if (!payloadSegment) return null;
  try {
    const parsed = JSON.parse(base64UrlDecode(payloadSegment));
    return parsed as JwtClaims;
  } catch {
    return null;
  }
}

export function rolesFromClaims(claims: JwtClaims | null): string[] {
  if (!claims) return [];
  if (Array.isArray(claims.roles)) return claims.roles;
  if (typeof claims.role === "string") return [claims.role];
  return [];
}
